import {
  Button as AriaButton,
  Select as AriaSelect,
  SelectValue as AriaSelectValue,
  Key,
  ListBoxItemRenderProps,
} from 'react-aria-components';
import { Typography } from '../../atoms/typography/typography';
import { twMerge } from 'tailwind-merge';
import { InputVariants } from '../../patterns/input';
import { ComponentProps, ReactElement } from 'react';
import { IconAngleDown } from '@maia/icons';
import { SelectDropdown } from '../../forms/selectDropdown/selectDropdown';
import { badgeStyles } from '../../patterns/badge';

type SelectItem<T> = {
  label: string;
  value: Key;
  meta: T;
};

type Props<T> = {
  items: Iterable<SelectItem<T>>;
  label?: string;
  value?: Key;
  enforceMinWidth?: boolean;
  enforceMaxHeight?: boolean;
  className?: string;
  onChange: (selection: Key) => void;
  renderSelectedValue?: (props: SelectItem<T>) => ReactElement | null;
  renderOption?: (
    _: SelectItem<T>,
    itemInfo: ListBoxItemRenderProps,
  ) => ReactElement | null;
} & Omit<
  ComponentProps<typeof AriaSelect>,
  'onSelectionChange' | 'onChange' | 'className'
> &
  InputVariants;

export function BadgeDropdown<T>(props: Props<T>) {
  const {
    label,
    items,
    size,
    value,
    enforceMinWidth,
    enforceMaxHeight,
    onChange,
    renderSelectedValue,
    renderOption,
    placeholder,
    className,
    ...rest
  } = props;

  return (
    <AriaSelect
      selectedKey={value}
      onSelectionChange={onChange}
      className={twMerge(className)}
      {...rest}
    >
      <AriaButton
        className={twMerge(
          badgeStyles({ variant: 'flat', spacing: 'default' }),
          'outline-none px-8',
        )}
      >
        <AriaSelectValue className="flex items-center gap-x-8">
          {(selectedValue) =>
            selectedValue.isPlaceholder || !renderSelectedValue ? (
              <>
                <Typography variant="body-xs" weight="regular" useCase="button">
                  {selectedValue.selectedText ?? placeholder}
                </Typography>
                <span className="" aria-hidden="true">
                  <IconAngleDown />
                </span>
              </>
            ) : renderSelectedValue ? (
              <>
                {renderSelectedValue(selectedValue.selectedItem as T)}
                <span className="" aria-hidden="true">
                  <IconAngleDown />
                </span>
              </>
            ) : null
          }
        </AriaSelectValue>
      </AriaButton>

      <SelectDropdown
        items={items}
        renderOption={renderOption}
        enforceMinWidth={enforceMinWidth}
        enforceMaxHeight={enforceMaxHeight}
      />
    </AriaSelect>
  );
}
